import { useState, useEffect, useRef, useId } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { contentEditorBlockIcon } from './contentEditorBlockPresentation.js';
import { searchContentEditorBlocks, contentEditorCategoryLabel } from './contentEditorRegistry.js';
import { CONTENT_BLOCK_DRAG_TYPE } from './contentEditorInteractions.js';

export function ContentEditorToolbox({ blocks, onInsert, disabled, focusRequest, canInsert }) {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [searchCollapsed, setSearchCollapsed] = useState({});
  const groupId = useId();
  const search = useRef(null);
  useEffect(() => { if (focusRequest) { setQuery(''); search.current?.focus(); } }, [focusRequest]);
  const groups = new Map();
  for (const block of searchContentEditorBlocks(blocks, query)) {
    const category = contentEditorCategoryLabel(block.category);
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(block);
  }
  return <aside aria-label="Content blocks" className="mts-editor-panel mts-editor-block-library"><h2>Blocks</h2>
    <div className="mts-editor-block-search"><Search size={16} aria-hidden="true"/>
      <input ref={search} aria-label="Search blocks" value={query} onChange={(event) => { setQuery(event.target.value); setSearchCollapsed({}); }} placeholder="Search blocks"/>
      {query ? <button type="button" aria-label="Clear block search" onClick={() => { setQuery(''); search.current?.focus(); }}><X size={16} aria-hidden="true"/></button> : null}
    </div>
    {!groups.size ? <p role="status">No blocks match “{query}”. Try another search.</p> : null}
    {[...groups].map(([category, items]) => {
      const searching = Boolean(query.trim());
      const expanded = !(searching ? searchCollapsed : collapsed)[category];
      const id = `${groupId}-${category.replace(/\s+/g, '-').toLowerCase()}`;
      return <section key={category} aria-label={category}>
      <h3 className="mts-editor-library-heading"><button type="button" className="mts-editor-library-group-toggle" aria-label={category} aria-expanded={expanded} aria-controls={id} onClick={() => (searching ? setSearchCollapsed : setCollapsed)(current => ({ ...current, [category]: !current[category] }))}><ChevronDown size={14} aria-hidden="true"/><span className="mts-editor-library-group-label">{category}</span><span>{items.length}</span></button></h3>
      <div id={id} className="mts-editor-block-grid" hidden={!expanded}>{items.map((block) => {
        const unavailable = Boolean(disabled || (canInsert && !canInsert(block)));
        const reason = disabled ? 'Editing is unavailable.' : 'This block cannot be inserted at the selected position.';
        const Icon = contentEditorBlockIcon(block.type, block.category);
        return <button key={block.id || block.type} type="button" className="mts-editor-block-tile" aria-label={block.label} draggable={!unavailable} disabled={unavailable}
          title={unavailable ? reason : block.description || block.label}
          onDragStart={(event) => { if (disabled || (canInsert && !canInsert(block))) { event.preventDefault(); return; } event.dataTransfer.setData(CONTENT_BLOCK_DRAG_TYPE, block.type); event.dataTransfer.effectAllowed = 'copy'; }}
          onClick={() => { if (!disabled && (!canInsert || canInsert(block))) onInsert(block); }}>
          <Icon size={20} aria-hidden="true"/><span>{block.label}</span>
          {unavailable ? <span className="sr-only">{reason}</span> : null}
        </button>;
      })}</div>
    </section>; })}
  </aside>;
}
